import {
  DialogContent,
  DialogTitle,
  ModalClose,
  Stack,
  Typography,
  Box,
  Badge,
  badgeClasses,
  SxProps,
} from '@castlery/fortress';

import { Divider } from '@castlery/fortress';

import { Drawer, drawerClasses } from '@castlery/fortress';

import { Button } from '@castlery/fortress';
import { FilterAlt } from '@castlery/fortress/Icons';
import { useEffect, useState, useRef } from 'react';
import { useClearRefinements, useCurrentRefinements, useInstantSearch } from 'react-instantsearch';
import { SortOption } from '../config/sort-options.config';
import { EcEnv } from '@castlery/config';
import { CustomCurrentRefinements } from '../instantsearch/custom-current-refinements';
import { CustomSortBy } from '../instantsearch/custom-sort-by';
import { FacetsContent } from '../instantsearch/facets-content';
import { formatNumberClient } from '@castlery/monorepo-i18n';
import { NextFortressLink } from '@castlery/shared-components';
import { FACET_ATTRIBUTE } from '../config';

export function MobileFilter({
  categoryFilter,
  sortOptions,
}: {
  categoryFilter?: string[];
  sortOptions: SortOption[];
}) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { items } = useCurrentRefinements();
  const { results } = useInstantSearch();
  const { refine: clear } = useClearRefinements({ excludedAttributes: [FACET_ATTRIBUTE.in_stock_regions] });

  const currentRefinementsCount = items.reduce((acc, item) => acc + item.refinements.length, 0);

  // Sticky behavior state and refs (disabled for POS)
  const isPosChannel = EcEnv.NEXT_PUBLIC_CHANNEL === 'POS';
  const [isSticky, setIsSticky] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);

  // Computed sticky state - combines all conditions for cleaner code
  const shouldEnableSticky = !isPosChannel;
  const shouldShowAsSticky = shouldEnableSticky && isSticky && !mobileFiltersOpen;
  const shouldShowPlaceholder = shouldEnableSticky && isSticky && !mobileFiltersOpen;

  // Computed styles for better maintainability
  const filterBarStyles: SxProps = {
    display: { xs: 'flex', md: 'none' },
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    py: 1,
    px: 6,
    // Sticky positioning
    position: shouldShowAsSticky ? 'fixed' : 'static',
    top: shouldShowAsSticky ? 0 : 'auto',
    left: shouldShowAsSticky ? 0 : 'auto',
    right: shouldShowAsSticky ? 0 : 'auto',
    zIndex: shouldShowAsSticky ? 1000 : 'auto',
    backgroundColor: 'var(--fortress-palette-background-surface)',
    borderBottom: '1px solid var(--fortress-palette-divider)',
    transition: 'all 0.2s ease-in-out',
    // Add shadow when sticky
    boxShadow: shouldShowAsSticky ? 'var(--fortress-shadow-sm)' : 'none',
  };

  const dividerStyles: SxProps = {
    display: { xs: 'flex', md: 'none' },
    // Make divider sticky along with the filter bar
    position: shouldShowAsSticky ? 'fixed' : 'static',
    top: shouldShowAsSticky ? `${filterBarHeight}px` : 'auto',
    left: shouldShowAsSticky ? 0 : 'auto',
    right: shouldShowAsSticky ? 0 : 'auto',
    zIndex: shouldShowAsSticky ? 999 : 'auto',
    backgroundColor: 'var(--fortress-palette-background-surface)',
    transition: 'all 0.2s ease-in-out',
  };

  // Store the original URL to restore on cancel
  const [initialUrl, setInitialUrl] = useState('');

  // Set up intersection observer for sticky behavior (only for non-POS)
  useEffect(() => {
    // Skip sticky behavior for POS
    if (!shouldEnableSticky) return;

    const observerElement = observerRef.current;
    const filterBarElement = filterBarRef.current;

    if (!observerElement || !filterBarElement) return;

    // Measure filter bar height for placeholder
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setFilterBarHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(filterBarElement);

    // Set up intersection observer
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // When the observer element is NOT intersecting (i.e., it's above the viewport)
        // the filter bar should become sticky
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    intersectionObserver.observe(observerElement);

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [shouldEnableSticky]);

  // Prevent body scrolling when drawer is open
  useEffect(() => {
    if (mobileFiltersOpen) {
      // Store current URL when opening drawer
      setInitialUrl(window.location.href);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileFiltersOpen]);

  // Function to handle drawer close without applying filters
  const handleCloseWithoutApply = () => {
    setMobileFiltersOpen(false);
    // Navigate back to initial state to discard changes
    if (initialUrl) {
      window.history.replaceState(null, '', initialUrl);
    }
  };

  // Function to apply filters and close drawer
  const handleApply = () => {
    setMobileFiltersOpen(false);
    // Changes are already applied to the URL, so we just need to close the drawer
  };

  // Function to handle reset - immediately clear all filters
  const handleReset = () => {
    clear();
    // Reset is immediate, no need to wait for Apply
    // Clear initialUrl since we're now in a clean state
    setInitialUrl('');
  };

  return (
    <>
      {/* Intersection Observer Target - positioned just above the filter bar */}
      {shouldEnableSticky && (
        <Box
          ref={observerRef}
          sx={{
            height: '1px',
            display: { xs: 'block', md: 'none' },
          }}
        />
      )}

      {/* Placeholder to maintain layout when filter bar becomes sticky */}
      {shouldShowPlaceholder && (
        <Box
          sx={{
            height: `${filterBarHeight}px`,
            display: { xs: 'block', md: 'none' },
          }}
        />
      )}

      {/* Mobile filter and sort controls - only visible on small screens */}
      <Box ref={filterBarRef} sx={filterBarStyles}>
        {/* Filters section */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <NextFortressLink
            variant="tertiary"
            color="neutral"
            level="subh2"
            sx={{ px: 0, textDecoration: 'none' }}
            startDecorator={
              <FilterAlt
                sx={{
                  ml: '-6px',
                }}
              />
            }
            component={'button'}
            onClick={() => setMobileFiltersOpen(true)}
          >
            Filters
          </NextFortressLink>
          <Badge
            badgeContent={currentRefinementsCount}
            sx={{
              display: currentRefinementsCount > 0 ? 'inline-flex' : 'none',
              [`.${badgeClasses.badge}`]: {
                position: 'static',
                transform: 'none',
              },
            }}
          />
        </Stack>
        {/* Sort section */}
        <CustomSortBy items={sortOptions} />
      </Box>

      {/* Divider - also becomes sticky when filter bar is sticky */}
      <Divider sx={dividerStyles} />
      {/* Mobile filters drawer */}
      <Drawer
        anchor="left"
        open={mobileFiltersOpen}
        onClose={handleCloseWithoutApply}
        sx={{
          display: { md: 'none' },
          [`.${drawerClasses.content}`]: {
            width: '100%',
          },
          '& .MuiDrawer-paper': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <ModalClose onClick={handleCloseWithoutApply} />
        <DialogTitle level="h1" component="span">
          Filters
        </DialogTitle>
        <Divider />

        <DialogContent
          sx={{
            flex: '1 1 auto',
            overflow: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            px: 6,
            py: 4,
          }}
        >
          {/* Selected filters at the top */}
          <Stack gap={2} pb={1}>
            <CustomCurrentRefinements />
          </Stack>

          {/* All filters */}
          <FacetsContent categoryFilter={categoryFilter} />
        </DialogContent>

        <Stack pt={4} px={6} pb={8} gap={3} bgcolor="var(--fortress-palette-brand-warmLinen-100)" boxShadow="md">
          <Typography>{`${formatNumberClient({ style: 'decimal' }).format(
            results.nbHits
          )} results available`}</Typography>
          <Stack direction="row" gap={4}>
            <Button size="sm" variant="outlined" fullWidth color="neutral" onClick={handleReset}>
              Reset
            </Button>
            <Button size="sm" fullWidth onClick={handleApply}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}
